// src/pages/Profile.tsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { getProfile, updateProfile } from "../redux/slices/authSlice"; // Import updateProfile
import { getKycStatus, submitKyc } from "../redux/slices/kycSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  User,
  Mail,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  Upload,
  Camera,
  FileText,
  Car,
  CreditCard,
  AlertCircle,
  Save,
  X,
  Edit,
} from "lucide-react";
import toast from "react-hot-toast";
import { formatFiat } from "../utils/currency";

const Profile: React.FC = () => {
  const dispatch = useDispatch();
  const { user, isLoading } = useSelector((state: RootState) => state.auth); // Get isLoading from auth
  const { kyc, isLoading: kycLoading } = useSelector(
    (state: RootState) => state.kyc
  );
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    username: "",
  });
  const [showKycForm, setShowKycForm] = useState(false);
  const [documentType, setDocumentType] = useState<
    "passport" | "id_card" | "driver_license"
  >("passport");
  const [documentFront, setDocumentFront] = useState<File | null>(null);
  const [documentBack, setDocumentBack] = useState<File | null>(null);
  const [selfiePhoto, setSelfiePhoto] = useState<File | null>(null);
  const [previewFront, setPreviewFront] = useState<string>("");
  const [previewBack, setPreviewBack] = useState<string>("");
  const [previewSelfie, setPreviewSelfie] = useState<string>("");

  useEffect(() => {
    dispatch(getProfile() as any);
    dispatch(getKycStatus() as any);
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        username: user.username || "",
      });
    }
  }, [user]);

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "front" | "back" | "selfie"
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      if (type === "front") {
        setDocumentFront(file);
        setPreviewFront(reader.result as string);
      } else if (type === "back") {
        setDocumentBack(file);
        setPreviewBack(reader.result as string);
      } else {
        setSelfiePhoto(file);
        setPreviewSelfie(reader.result as string);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSaveProfile = async () => {
    try {
      // Filter out empty fields
      const updateData: any = {};
      if (formData.first_name.trim())
        updateData.first_name = formData.first_name.trim();
      if (formData.last_name.trim())
        updateData.last_name = formData.last_name.trim();
      if (formData.username.trim())
        updateData.username = formData.username.trim();

      // Check if there's anything to update
      if (Object.keys(updateData).length === 0) {
        toast.error("No changes made");
        return;
      }

      // Call the updateProfile action
      await dispatch(updateProfile(updateData) as any);

      toast.success("Profile updated successfully");
      setEditMode(false);

      // Refresh profile data to get latest from server
      dispatch(getProfile() as any);
    } catch (error: any) {
      toast.error(error.message || "Failed to update profile");
    }
  };

  const handleKycSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!documentFront || !selfiePhoto) {
      toast.error("Please upload all required documents");
      return;
    }

    if (documentType !== "passport" && !documentBack) {
      toast.error("Please upload both sides of your document");
      return;
    }

    const formData = new FormData();
    formData.append("document_type", documentType);
    formData.append("document_front", documentFront);
    if (documentBack) formData.append("document_back", documentBack);
    formData.append("selfie_photo", selfiePhoto);

    try {
      await dispatch(submitKyc(formData) as any);
      toast.success("KYC submitted successfully!");
      setShowKycForm(false);
      resetKycForm();
    } catch (error) {
      toast.error("Failed to submit KYC");
    }
  };

  const resetKycForm = () => {
    setDocumentFront(null);
    setDocumentBack(null);
    setSelfiePhoto(null);
    setPreviewFront("");
    setPreviewBack("");
    setPreviewSelfie("");
    setDocumentType("passport");
  };

  const getDocumentTypeIcon = (type: string) => {
    switch (type) {
      case "passport":
        return <FileText className="h-5 w-5" />;
      case "id_card":
        return <CreditCard className="h-5 w-5" />;
      case "driver_license":
        return <Car className="h-5 w-5" />;
      default:
        return <FileText className="h-5 w-5" />;
    }
  };

  if (!user) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className={`${GlobalStyles.spinner} h-8 w-8`}></div>
      </div>
    );
  }

  return (
    <div className={GlobalStyles.pageContainer}>
      <div className={GlobalStyles.contentContainer}>
        {/* Header */}
        <div className={GlobalStyles.sectionSpacing}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className={GlobalStyles.heading1}>My Profile</h1>
              <p className={GlobalStyles.bodyText + " mt-2"}>
                Manage your account information and verification
              </p>
            </div>
            <div className="flex items-center gap-3">
              {editMode ? (
                <>
                  <button
                    onClick={() => {
                      setEditMode(false);
                      // Reset form data to original user data
                      setFormData({
                        first_name: user.first_name || "",
                        last_name: user.last_name || "",
                        username: user.username || "",
                      });
                    }}
                    className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium}`}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveProfile}
                    disabled={isLoading}
                    className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonMedium} disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isLoading ? (
                      <>
                        <div
                          className={`${GlobalStyles.spinner} h-4 w-4 mr-2`}
                        ></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setEditMode(true)}
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonMedium}`}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Profile
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Profile Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Information */}
            <div className={GlobalStyles.card}>
              <h3 className={`${GlobalStyles.heading3} mb-6`}>
                Personal Information
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={GlobalStyles.inputLabel}>
                      First Name
                    </label>
                    {editMode ? (
                      <input
                        type="text"
                        value={formData.first_name}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            first_name: e.target.value,
                          })
                        }
                        className={GlobalStyles.input}
                        placeholder="Enter first name"
                      />
                    ) : (
                      <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                        <User className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-gray-900 dark:text-white">
                          {user.first_name || "Not set"}
                        </span>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className={GlobalStyles.inputLabel}>Last Name</label>
                    {editMode ? (
                      <input
                        type="text"
                        value={formData.last_name}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            last_name: e.target.value,
                          })
                        }
                        className={GlobalStyles.input}
                        placeholder="Enter last name"
                      />
                    ) : (
                      <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                        <User className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-gray-900 dark:text-white">
                          {user.last_name || "Not set"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className={GlobalStyles.inputLabel}>Email</label>
                  <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <Mail className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-gray-900 dark:text-white">
                      {user.email}
                    </span>
                    {user.email_verified_at && (
                      <CheckCircle className="h-4 w-4 text-green-500 ml-2" />
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Email cannot be changed
                  </p>
                </div>

                <div>
                  <label className={GlobalStyles.inputLabel}>Username</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) =>
                        setFormData({ ...formData, username: e.target.value })
                      }
                      className={GlobalStyles.input}
                      placeholder="Enter username"
                    />
                  ) : (
                    <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                      <User className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-gray-900 dark:text-white">
                        {user.username || "Not set"}
                      </span>
                    </div>
                  )}
                </div>

                <div>
                  <label className={GlobalStyles.inputLabel}>
                    Member Since
                  </label>
                  <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-gray-900 dark:text-white">
                      {user.created_at
                        ? new Date(user.created_at).toLocaleDateString()
                        : "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* KYC Verification */}
            {!kyc && user.verification_level === 1 && (
              <div className={GlobalStyles.card}>
                <div className="text-center mb-6">
                  <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <Shield className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className={GlobalStyles.heading2 + " mb-2"}>
                    Complete KYC Verification
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Verify your identity to unlock all platform features
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-center">
                    <div className="w-10 h-10 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-3 mx-auto">
                      <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Higher Limits
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Increased transaction limits
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-center">
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mb-3 mx-auto">
                      <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Enhanced Security
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Added protection for your account
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-center">
                    <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center mb-3 mx-auto">
                      <User className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Full Access
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Access to all platform features
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => setShowKycForm(true)}
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonLarge} w-full`}
                >
                  <Shield className="h-5 w-5 mr-2" />
                  Start KYC Verification
                </button>
              </div>
            )}
          </div>

          {/* Right Column - Verification Status */}
          <div className="space-y-6">
            {/* Verification Status */}
            <div className={GlobalStyles.card}>
              <h3 className={`${GlobalStyles.heading3} mb-6`}>
                Verification Status
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Email Verification
                  </span>
                  {user.email_verified_at ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500" />
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    KYC Level 1
                  </span>
                  {user.verification_level >= 1 ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <Clock className="h-5 w-5 text-yellow-500" />
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    KYC Level 2
                  </span>
                  {user.verification_level >= 2 ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <Clock className="h-5 w-5 text-yellow-500" />
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    KYC Level 3
                  </span>
                  {user.verification_level >= 3 ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <Clock className="h-5 w-5 text-yellow-500" />
                  )}
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center">
                  <Shield className="h-5 w-5 text-primary mr-2" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Level {user.verification_level}/3
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Complete verification to unlock all features
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Account Statistics */}
            <div className={GlobalStyles.card}>
              <h3 className={`${GlobalStyles.heading3} mb-6`}>
                Account Statistics
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Account Balance
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatFiat(user.balance)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Verification Level
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {user.verification_level}/3
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Wallet Status
                  </span>
                  <span
                    className={`font-medium ${
                      user.wallet_connected
                        ? "text-green-600 dark:text-green-400"
                        : "text-red-600 dark:text-red-400"
                    }`}
                  >
                    {user.wallet_connected ? "Connected" : "Not Connected"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Account Age
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {user.created_at
                      ? Math.floor(
                          (new Date().getTime() -
                            new Date(user.created_at).getTime()) /
                            (1000 * 60 * 60 * 24)
                        ) + " days"
                      : "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* KYC Form Modal */}
        {showKycForm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-5">
            <div
              className={`${GlobalStyles.card} max-w-2xl w-full max-h-[90vh] overflow-y-auto`}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className={GlobalStyles.heading2}>KYC Verification</h3>
                <button
                  onClick={() => {
                    setShowKycForm(false);
                    resetKycForm();
                  }}
                  className={GlobalStyles.iconButton}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleKycSubmit} className="space-y-6">
                {/* Document Type */}
                <div>
                  <label className={GlobalStyles.inputLabel}>
                    Select Document Type
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {["passport", "id_card", "driver_license"].map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setDocumentType(type as any)}
                        className={`p-4 rounded-lg border flex flex-col items-center justify-center transition-colors ${
                          documentType === type
                            ? "border-primary bg-primary/5"
                            : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                        }`}
                      >
                        {getDocumentTypeIcon(type)}
                        <span className="mt-2 font-medium capitalize">
                          {type.replace("_", " ")}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Document Front */}
                <div>
                  <label className={GlobalStyles.inputLabel}>
                    Document Front
                  </label>
                  <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg p-8 hover:border-primary transition-colors">
                    {previewFront ? (
                      <div className="relative">
                        <img
                          src={previewFront}
                          alt="Document Front"
                          className="w-full h-auto rounded-lg max-h-64 object-contain"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setDocumentFront(null);
                            setPreviewFront("");
                          }}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center cursor-pointer">
                        <Upload className="h-12 w-12 text-gray-400 mb-4" />
                        <p className="text-gray-600 dark:text-gray-400 mb-2">
                          Upload clear photo of document front
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                          JPG, PNG (Max 5MB)
                        </p>
                        <div
                          className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium}`}
                        >
                          Select File
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleFileChange(e, "front")}
                            required
                          />
                        </div>
                      </label>
                    )}
                  </div>
                </div>

                {/* Document Back (if not passport) */}
                {documentType !== "passport" && (
                  <div>
                    <label className={GlobalStyles.inputLabel}>
                      Document Back
                    </label>
                    <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg p-8 hover:border-primary transition-colors">
                      {previewBack ? (
                        <div className="relative">
                          <img
                            src={previewBack}
                            alt="Document Back"
                            className="w-full h-auto rounded-lg max-h-64 object-contain"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setDocumentBack(null);
                              setPreviewBack("");
                            }}
                            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center cursor-pointer">
                          <Upload className="h-12 w-12 text-gray-400 mb-4" />
                          <p className="text-gray-600 dark:text-gray-400 mb-2">
                            Upload clear photo of document back
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                            JPG, PNG (Max 5MB)
                          </p>
                          <div
                            className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium}`}
                          >
                            Select File
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleFileChange(e, "back")}
                            />
                          </div>
                        </label>
                      )}
                    </div>
                  </div>
                )}

                {/* Selfie Photo */}
                <div>
                  <label className={GlobalStyles.inputLabel}>
                    Selfie with Document
                  </label>
                  <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg p-8 hover:border-primary transition-colors">
                    {previewSelfie ? (
                      <div className="relative">
                        <img
                          src={previewSelfie}
                          alt="Selfie"
                          className="w-full h-auto rounded-lg max-h-64 object-contain"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setSelfiePhoto(null);
                            setPreviewSelfie("");
                          }}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center cursor-pointer">
                        <Camera className="h-12 w-12 text-gray-400 mb-4" />
                        <p className="text-gray-600 dark:text-gray-400 mb-2">
                          Take a selfie while holding your document
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                          Face and document must be clearly visible
                        </p>
                        <div
                          className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium}`}
                        >
                          Take Photo
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleFileChange(e, "selfie")}
                            capture="environment"
                            required
                          />
                        </div>
                      </label>
                    )}
                  </div>
                </div>

                {/* Requirements */}
                <div
                  className={`${GlobalStyles.alert} ${GlobalStyles.alertInfo}`}
                >
                  <AlertCircle className="h-5 w-5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium">
                      Verification Requirements:
                    </p>
                    <ul className="text-sm mt-2 space-y-1">
                      <li>• All documents must be valid and not expired</li>
                      <li>• Photos must be clear and all text readable</li>
                      <li>
                        • Selfie must clearly show your face and the document
                      </li>
                      <li>• All information must match your account details</li>
                    </ul>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowKycForm(false);
                      resetKycForm();
                    }}
                    className={`${GlobalStyles.button} ${GlobalStyles.buttonGhost} ${GlobalStyles.buttonMedium}`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={kycLoading}
                    className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonMedium}`}
                  >
                    {kycLoading ? (
                      <div className="flex items-center">
                        <div
                          className={`${GlobalStyles.spinner} w-4 h-4 mr-2`}
                        ></div>
                        Submitting...
                      </div>
                    ) : (
                      <>
                        <Shield className="h-5 w-5 mr-2" />
                        Submit for Verification
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
